import { useContext, useMemo } from 'react'

import { launchModal, sortArrayBy } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useAgencySelector from '@hooks/useAgencySelector'

import PageContext from '@contexts/pageContext'
import useSelector from '@hooks/useSelector'
import useTags from '@hooks/useTags'
import useTemplatePageCategories from '@hooks/useTemplatePageCategories'
import useTemplatePages from '@hooks/useTemplatePages'

import type { TagModel, TemplatePageCategoryModel, TemplatePageModel } from '@models/types'
import type { EntitiesState } from '@redux/entities'

const watchEntityKeys = [
  'agencies',
  'providers',
  'serviceProducts',
  'templatePageCategories',
  'templatePages',
]

type FindCategoryIdParams = {
  selectedCategoryId?: number,
  templatePage: TemplatePageModel,
}

const findCategoryId = (params: FindCategoryIdParams) => {
  const { selectedCategoryId, templatePage } = params

  if (selectedCategoryId) return selectedCategoryId

  const categories: Array<number> = templatePage?.categories || []
  return categories.length === 1 ? categories[0] : undefined
}

type GenerateUrlsParams = {
  selectedAgencyId?: number,
  tag?: TagModel,
}

const generateUrls = (params: GenerateUrlsParams) => {
  const { selectedAgencyId, tag } = params

  return {
    agencyAdminManageTemplatesUrl: selectedAgencyId ? `#/agencies/${selectedAgencyId}/manage/templateGallery` : '',
    agencyMarketingIndexUrl: '#/agency/marketing',
    agencyMarketingSelectTemplateUrl: `#/agency/marketing/templates?tagId=${tag?.id}`,
  }
}

const selectTag = (tag: TagModel) => {
  if (tag.id){
    const { agencyMarketingSelectTemplateUrl } = generateUrls({ tag })

    window.location.hash = agencyMarketingSelectTemplateUrl
  }
}

type FindAvailableCategoryIdsProps = {
  templatePages: TemplatePageModel[],
  tagId?: number,
}

const findAvailableCategoryIds = (props: FindAvailableCategoryIdsProps) => {
  const { templatePages, tagId } = props

  const categoryIds: number[] = []

  templatePages.forEach((templatePage: TemplatePageModel) => {
    const tags: Array<number> = templatePage?.tags || []

    if (tags?.includes(tagId!)){
      const categories: Array<number> = templatePage?.categories || []

      categories.forEach((categoryId: number) => {
        if (!categoryIds.includes(categoryId)){
          categoryIds.push(categoryId)
        }
      })
    }
  })

  return categoryIds
}

type FindAvailableCategoriesProps = {
  templatePageCategories: TemplatePageCategoryModel[],
  templatePages: TemplatePageModel[],
  tagId?: number,
}

const findAvailableCategories = (props: FindAvailableCategoriesProps) => {
  const { templatePageCategories, templatePages, tagId } = props || {}

  const availableCategoryIds = findAvailableCategoryIds({ templatePages, tagId })
  const filtered = templatePageCategories.filter(category => availableCategoryIds.includes(category.id))

  return sortArrayBy(filtered, 'asc', 'title')
}

type GroupTemplatePagesParams = {
  filteredTemplatePages: TemplatePageModel[],
  serviceProducts: EntitiesState['serviceProducts'],
}

const groupTemplatePages = (params: GroupTemplatePagesParams) => {
  const { serviceProducts, filteredTemplatePages } = params || {}

  const groups = {}

  filteredTemplatePages.forEach((templatePage) => {
    const { service_product_ids = [] } = templatePage

    // Loop through each product
    service_product_ids.forEach((productId) => {
      const product = serviceProducts[productId]

      // Create group if it doesn't exist
      // @ts-expect-error TS7053
      if (!groups[productId]){
        // @ts-expect-error TS7053
        groups[productId] = {
          id: productId,
          items: [],
          title: product.title || `Product #${productId}`,
        }
      }

      // Add Template Page to group
      // A page can belong to multiple product groups
      // Dont add to a group where it already exists
      // @ts-expect-error TS7053
      if (!groups[productId].items.find(t => t.id === templatePage.id)){
        // @ts-expect-error TS7053
        groups[productId].items.push(templatePage)
      }
    })
  })

  return groups
}

export type CampaignParams = {
  categoryId?: number,
  createCampaignWithArtwork?: boolean,
  hideComponents?: Array<'ExistingCampaign' | 'NewCampaign'>,
  isCanvaTemplate?: boolean,
  selectedAgency?: number,
  selectedProviderPriceListItem?: number,
  selectedProviderPriceListItemOptions?: object,
  selectedTemplatePageId?: number,
}

export type OrderParams = {
  categoryId?: number,
  templatePage: TemplatePageModel,
  serviceProductId: number,
}

type AgencyMarketingOptions = {
  filters?: {
    category_id?: number,
    string?: string,
    tag_id?: number,
  },
  tag?: TagModel,
}

const useAgencyMarketing = (options: AgencyMarketingOptions = {}) => {
  const { tag, filters } = options
  const {
    category_id, tag_id: filterTagId,
  } = filters || {}

  const {
    updatedEntities: {
      serviceProducts: serviceProductsUpdatedAt,
      templatePageCategories: templatePageCategoriesUpdatedAt,
      templatePages: templatePagesUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const agencySelectorPayload = useAgencySelector()
  const {
    selectedAgency,
    selectedAgencyId,
  } = agencySelectorPayload

  const templatePageCategoriesPayload = useTemplatePageCategories({ performHttpRequests: true })
  const {
    filteredTemplatePageCategories,
    loading: loadingTemplatePageCategories,
  } = templatePageCategoriesPayload || {}

  // Load Template Pages when tag is selected
  const templatePagesPayload = useTemplatePages({
    filters: { ...filters, brand_theme_id: selectedAgency?.brand_theme_id },
    performHttpRequests: !!filterTagId,
    requestOptions: {
      include_template_page_custom_preview: true,
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
      include_template_page_service_products: true,
    },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading: loadingTemplatePages,
  } = templatePagesPayload

  const tagsPayload = useTags({
    requestOptions: {
      include_tag_image: true,
      include_template_pages_count: true,
      usable_tags_only: true,
    },
    performHttpRequests: true,
  })

  const {
    filteredTags,
    loading: loadingTags,
  } = tagsPayload

  const featuredTags = filteredTags.filter(filteredTag => filteredTag.title?.match(/^\*/))
  const hasFeaturedTags = !!featuredTags.length

  const { serviceProducts } = useSelector(reduxState => reduxState.entities)

  const availableCategories = useMemo(() => (
    findAvailableCategories({
      templatePageCategories: filteredTemplatePageCategories,
      templatePages: filteredTemplatePages,
      tagId: filterTagId,
    })
  ), [filterTagId, templatePagesUpdatedAt, templatePageCategoriesUpdatedAt])

  // Grouped Template Pages
  const groupedTemplatePages = useMemo(() => {
    const grouped = groupTemplatePages({ serviceProducts, filteredTemplatePages })
    const sorted = sortArrayBy(Object.values(grouped), 'asc')
    return sorted
  }, [filters, loadingTemplatePages, serviceProductsUpdatedAt, templatePagesUpdatedAt])

  const hasGroupedTemplatePages = hasFilteredTemplatePages && !!groupedTemplatePages.length

  const { callbacks } = useContext(PageContext)

  return {
    availableCategories,
    callbacks: {
      addAgencyMarketingCampaign: (
        agencyMarketingCampaignParams: CampaignParams,
      ) => launchModal({
        callbacks,
        modalKey: 'CreateAgencyMarketingCampaignModal',
        payload: { agencyMarketingCampaignParams },
      }),
      createAgencyMarketingOrder: (orderPayload: OrderParams) => launchModal({
        callbacks,
        modalKey: 'CreateAgencyMarketingOrderModal',
        payload: { orderPayload },
      }),
      findCategoryId: (templatePage: TemplatePageModel) => (
        findCategoryId({ selectedCategoryId: category_id, templatePage })
      ),
      loadMore,
      selectTag: (selectedTag: TagModel) => selectTag(selectedTag),
    },
    canLoadMore,
    featuredTags,
    filteredTemplatePagesCount,
    groupedTemplatePages,
    hasFeaturedTags,
    hasGroupedTemplatePages,
    loading: loadingTemplatePageCategories && loadingTemplatePages && loadingTags,
    selectedAgency,
    selectedAgencyId,
    tagsPayload,
    urls: generateUrls({ selectedAgencyId, tag }),
  }
}

export type UseAgencyMarketingPayload = ReturnType<typeof useAgencyMarketing>

export default useAgencyMarketing
